import { observer } from 'mobx-react';
import React from 'react';
import { makeAutoObservable } from 'mobx';
import API, {
	IDocumentSearchResult,
	IEntityColumn,
	IUserConfigPackage,
} from '../../../API';
import {
	ContextualMenu,
	DetailsList,
	DetailsListLayoutMode,
	DirectionalHint,
	IColumn,
	IconButton,
	mergeStyleSets,
	SelectionMode,
	Spinner,
	SpinnerSize,
} from '@fluentui/react';
import EntityDashboard from '../../dashboards/EntityDashboard';
import LegalEntityDocument from '../../../outlook/LegalEntityDocument';

export interface IDocumentSearchResultsProps {
	state: DocumentSearchResultsState;
}

const classNames = mergeStyleSets({
	tasksSummaryTableRow: {
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer'
	},
});

class DocumentSearchResults extends React.Component<
	IDocumentSearchResultsProps,
	{}
> {

	render() {
		if (this.props.state.loading) {
			return <Spinner size={SpinnerSize.large} />;
		}

		if (!this.props.state.results) {
			return <></>;
		}

		if (this.props.state.results.items.length === 0)
			return <>No matching documents.</>;

		return <>{this.getItemList()}</>;
	}

	private _getColumns(): IColumn[] {
		return this.props.state.columns.filter(c => !c.isHidden).map((column) => {
			return {
				key: column.id,
				name: column.displayName,
				fieldName: column.id,
				minWidth: column.displayWidth || 200,
				isResizable: true,
				className: '',
				onRender: (item: any) => {
					if (column) {

						if (column.id === 'menu') {
							//Show an icon that looks like an ellipsis
							return <IconButton
								iconProps={{ iconName: 'More' }}
								title="Actions"
								ariaLabel="Actions"
								onClick={(ev) => {
									ev.stopPropagation();
									//need to select the item?
									this.props.state.setMenuDomTarget(ev.target as HTMLElement);
									this.props.state.setContextMenuShownForItem(item);
								}}
							/>;
						} else if (column.id === 'icon') {
							return LegalEntityDocument.getIcon(item);
						}


						var taskCustomFieldValue = item[column.id];

						if (taskCustomFieldValue !== undefined) {
							taskCustomFieldValue = EntityDashboard.displayPropertyValue(
								null,
								this.props.state.userConfig,
								{} as IEntityColumn,
								taskCustomFieldValue,
								true,
							);
						}

						if (taskCustomFieldValue !== undefined) {
							return (
								<div
									key={column.id}
									className={classNames.tasksSummaryTableRow}
									onClick={() => { this.props.state.itemSelectedForPreview(item) }}
								>
									<span>{taskCustomFieldValue}</span>
								</div>
							);
						}
					}
				},
			};
		});
	}

	private getItemList(): React.ReactNode {

		var columns = this._getColumns();

		columns.forEach((column) => {

			if (column.fieldName === 'menu' || column.fieldName === 'icon') {
				return;
			}

			column.isSorted = this.props.state.sortField === column.fieldName;
			column.isSortedDescending = !this.props.state.sortAsc;
			column.onColumnClick = (_, c) => {
				if (c.fieldName === this.props.state.sortField) {
					this.props.state.setSorting(c.fieldName, !this.props.state.sortAsc);
				} else {
					//Set the sort direction to the default (ascending) and the field to the new field
					this.props.state.setSorting(c.fieldName || '', true);
				}
			};
		});

		return (
			<>
				{this.props.state.menuDomTarget &&
					<ContextualMenu
						items={[
							{
								key: 'preview', text: 'Preview', onClick: () => {
									this.props.state.itemSelectedForPreview(this.props.state.contextMenuShownForItem);
									this.props.state.setMenuDomTarget(undefined);
								}
							},
							{
								key: 'attachToEmail', text: 'Attach to Email', onClick: () => {
									this.props.state.setMenuDomTarget(undefined);
									this.props.state.itemSelectedForAttachToEmail(this.props.state.contextMenuShownForItem);
								}
							}
						]}
						hidden={this.props.state.menuDomTarget === undefined}
						target={this.props.state.menuDomTarget}
						onItemClick={(_, item) => this.props.state.setMenuDomTarget(undefined)}
						onDismiss={() => this.props.state.setMenuDomTarget(undefined)}
						directionalHint={DirectionalHint.bottomLeftEdge}
					/>
				}
				<DetailsList
					compact={true}
					isHeaderVisible={true}
					layoutMode={DetailsListLayoutMode.fixedColumns}
					setKey="items"
					items={this.props.state.results?.items || []}
					columns={columns}
					selectionMode={SelectionMode.none}
					ariaLabelForGrid="Item details"
					onItemInvoked={(item: any) => {
						//launch the doc viewer
						this.props.state.itemSelectedForPreview(item);
					}}
				/>
			</>
		);
	}
}

export class DocumentSearchResultsState {
	public userConfig: IUserConfigPackage;
	private searchText: string = '';

	private onItemLaunch: (item: any) => void;
	private onAttachToEmail: (item: any) => void;
	onLoad?: () => void;
	includeOneDriveResults: boolean;
	contextMenuShownForItem: any;

	constructor(
		userConfig: IUserConfigPackage,
		includeOneDriveResults: boolean, //OneDrive results mean using graph, which doesn't allow returning sharepoint managed properties
		onItemLaunch: (item: any) => void,
		onAttachToEmail: (item: any) => void,
		onLoad?: () => void
	) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
		this.onItemLaunch = onItemLaunch;
		this.onAttachToEmail = onAttachToEmail;
		this.onLoad = onLoad;
		this.includeOneDriveResults = includeOneDriveResults;
	}

	public menuDomTarget: HTMLElement | undefined = undefined;
	public setMenuDomTarget(target: HTMLElement | undefined) {
		this.menuDomTarget = target;
	}

	public setContextMenuShownForItem(item: any) {
		this.contextMenuShownForItem = item;
	}

	public itemSelectedForPreview(item: any) {
		this.onItemLaunch(item);
	}

	public itemSelectedForAttachToEmail(contextMenuShownForItem: any) {
		this.onAttachToEmail(contextMenuShownForItem);
	}


	public loading: boolean = false;
	public setLoading(val: boolean) {
		this.loading = val;
	}

	public sortField: string = '';
	public sortAsc: boolean = true;

	public setSorting(sortField: string, asc: boolean) {
		this.sortField = sortField;
		this.sortAsc = asc;

		this.runSearch();
	}

	public columns: IEntityColumn[] = [];
	public setColumns(columns: IEntityColumn[]) {
		this.columns = columns;
	}

	public searchScopeId: string = '';
	public setSearchScope(val: string) {
		this.searchScopeId = val;
		this.runSearch();
	}

	public showAllItems() {
		this.searchScopeId = '';
		this.runSearch();
	}

	public setSearchText(searchText: string) {
		this.searchText = searchText;
		this.runSearch();
	}

	public hasSearchQuery() {
		return this.searchText || this.searchScopeId;
	}

	private async runSearch(): Promise<void> {
		let searchKey = this.getSearchKey();
		this.setResults(); //Clear the results

		this.setLoading(true);

		let api = new API(await this.userConfig.getToken());
		let results = await api.documentSearch({
			searchText: this.searchText,
			sortBy: this.sortField,
			sortAsc: this.sortAsc,
			searchScopeId: this.searchScopeId,
			includeOneDriveResult: this.includeOneDriveResults
		});

		//If no changes have been made to the search criteria
		if (searchKey === this.getSearchKey()) {
			this.setResults(results);
			this.setLoading(false);
		}
	}

	public results?: IDocumentSearchResult;
	public setResults(results?: IDocumentSearchResult) {
		this.results = results;
		this.setColumns(results?.columns || []);

		if (this.onLoad)
			this.onLoad();
	}

	private getSearchKey() {
		return this.sortField + this.sortAsc + this.searchText + this.searchScopeId;
	}
}

export default observer(DocumentSearchResults);
